import React from 'react';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const MASKED_PASSWORD = '••••••••';
const MASKED_SECURITY = '••••';

const LIGHT_PREVIEW_THEME = {
  text: {
    primary: '#14532d',
    secondary: '#166534',
    muted: '#15803d',
  },
};

export const MaskedRevealField = ({
  label,
  maskedValue,
  fieldKey,
  saved = true,
  canReveal = false,
  isRevealed = false,
  displayValue,
  loading = false,
  onToggleReveal,
  theme,
}) => {
  const { t } = useI18n();
  const CR = 'creditReport';
  const value = displayValue ?? maskedValue ?? '—';

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs shrink-0" style={{ color: theme?.text?.muted || '#9CA3AF' }}>
        {label}
      </span>
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="text-sm font-mono tracking-wider truncate"
          style={{ color: theme?.text?.secondary || '#E5E7EB' }}
          data-testid={`masked-field-${fieldKey}`}
        >
          {saved ? value : '—'}
        </span>
        {saved && canReveal && (
          <button
            type="button"
            onClick={() => onToggleReveal?.(fieldKey)}
            disabled={loading}
            className="shrink-0 p-1 rounded-md hover:bg-white/5 transition-colors"
            style={{ color: theme?.text?.muted || '#9CA3AF' }}
            aria-label={isRevealed ? 'Hide value' : 'Reveal value'}
            data-testid={`reveal-toggle-${fieldKey}`}
          >
            {loading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : isRevealed ? (
              <EyeOff size={14} />
            ) : (
              <Eye size={14} />
            )}
          </button>
        )}
        {saved && !canReveal && (
          <span
            className="text-[10px] max-w-[120px] text-right leading-tight"
            style={{ color: theme?.text?.muted || '#9CA3AF' }}
            title={t(`${CR}.revealPermissionDenied`)}
          >
            {t(`${CR}.revealPermissionDeniedShort`)}
          </span>
        )}
      </div>
    </div>
  );
};

const SavedCredentialPreview = ({
  credInfo,
  canReveal = false,
  revealBlockedReason,
  reveal,
  theme,
  variant = 'dark',
  className = '',
}) => {
  const { t } = useI18n();
  const CR = 'creditReport';
  const isLight = variant === 'light';
  const previewTheme = isLight ? LIGHT_PREVIEW_THEME : theme;

  if (!credInfo) return null;

  const passwordMasked = credInfo.passwordSaved
    ? (credInfo.passwordMasked || MASKED_PASSWORD)
    : null;
  const securityMasked = credInfo.securityAnswerSaved
    ? (credInfo.securityAnswerMasked || MASKED_SECURITY)
    : null;

  return (
    <div
      className={`p-4 rounded-xl space-y-3 ${isLight ? 'bg-emerald-50 border border-emerald-200' : ''} ${className}`}
      style={isLight ? undefined : {
        background: theme?.surface?.s2 || '#0D1F1A',
        border: `1px solid ${theme?.border?.b2 || '#2D4A44'}`,
      }}
      data-testid="saved-credential-preview"
    >
      <div className="flex items-center gap-2 mb-1">
        <CheckCircle size={16} className="text-emerald-500" />
        <span
          className={`text-sm font-medium ${isLight ? 'text-emerald-900' : ''}`}
          style={isLight ? undefined : { color: theme?.text?.primary || '#fff' }}
        >
          {t(`${CR}.savedCredentialsFound`)}
        </span>
      </div>
      <MaskedRevealField
        label="Email / Username"
        maskedValue={credInfo.usernameMasked}
        fieldKey="username"
        saved
        canReveal={false}
        displayValue={credInfo.usernameMasked}
        theme={previewTheme}
      />
      <MaskedRevealField
        label="Password"
        maskedValue={passwordMasked}
        fieldKey="providerPassword"
        saved={credInfo.passwordSaved}
        canReveal={canReveal && credInfo.passwordSaved}
        isRevealed={reveal?.isRevealed('providerPassword')}
        displayValue={reveal?.getDisplayValue('providerPassword', passwordMasked)}
        loading={reveal?.revealLoading === 'providerPassword'}
        onToggleReveal={reveal?.requestReveal}
        theme={previewTheme}
      />
      <MaskedRevealField
        label="Last 4 of SSN or Security Answer"
        maskedValue={securityMasked}
        fieldKey="providerSecurityAnswer"
        saved={credInfo.securityAnswerSaved}
        canReveal={canReveal && credInfo.securityAnswerSaved}
        isRevealed={reveal?.isRevealed('providerSecurityAnswer')}
        displayValue={reveal?.getDisplayValue('providerSecurityAnswer', securityMasked)}
        loading={reveal?.revealLoading === 'providerSecurityAnswer'}
        onToggleReveal={reveal?.requestReveal}
        theme={previewTheme}
      />
      {!canReveal && revealBlockedReason && (
        <p className="text-[11px] pt-1" style={{ color: previewTheme?.text?.muted || '#9CA3AF' }}>
          {revealBlockedReason}
        </p>
      )}
      {reveal?.revealError && (
        <p
          className="text-[11px] pt-1"
          style={{ color: previewTheme?.text?.muted || '#9CA3AF' }}
          data-testid="reveal-error-message"
        >
          {reveal.revealError}
        </p>
      )}
      {credInfo.lastUsedAt && (
        <p
          className="text-xs pt-2 border-t"
          style={{
            borderColor: isLight ? '#bbf7d0' : (theme?.border?.b2 || '#2D4A44'),
            color: previewTheme?.text?.muted || '#9CA3AF',
          }}
        >
          {t(`${CR}.lastUsedLabel`, {
            date: new Date(credInfo.lastUsedAt).toLocaleDateString(),
          })}
        </p>
      )}
    </div>
  );
};

export default SavedCredentialPreview;
